import { groupNamePresenter, taskNamePresenter } from '@kaiizen/kai-presenters';
import {
  getAllScanTaskTemplatesForGroup,
  getEffectiveScanValue,
  getScanTask,
  getTotalCost,
  isLineItemScoped,
  ScanLineItemOptimized,
  ScanOptionOptimized,
  ScanPartOptimized,
  ScanRootOptimized,
  ScanTaskOptimized,
  ScanTemplateLineItem,
  ScanTemplateOption,
  ScanTemplateSkuOption,
  ScanTemplateTask,
  ScanTemplateValues,
} from '@kaiizen/kai-scan-template-system';

import type {
  VoiceScopeCatalogOption,
  VoiceScopeCatalogTask,
  VoiceScopeSnapshot,
  VoiceScopeSnapshotOption,
  VoiceScopeSnapshotTask,
  VoiceScopeSnapshotValue,
} from './voiceScopeTypes';

const NON_NUMERIC_VALUE_TYPES = new Set(['text', 'checkbox']);
const LINE_ITEM_SPOKEN_VALUES: Record<string, string> = { quantity: 'quantity', price: 'price', laborPrice: 'labor price' };
const HTML_ENTITIES: Record<string, string> = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&apos;': "'", '&nbsp;': ' ' };

const decodeEntities = (text: string) => text.replace(/&(?:amp|lt|gt|quot|#39|apos|nbsp);/g, entity => HTML_ENTITIES[entity] ?? entity);
const decodeOptional = (text: string | undefined) => (text === undefined ? undefined : decodeEntities(text));

type VoiceScopeProduct = { title?: string; description?: string };
export type VoiceScopeProductOf = (skuOption: ScanTemplateSkuOption | string | undefined) => VoiceScopeProduct | undefined;

const noProducts: VoiceScopeProductOf = () => undefined;

export const voiceScopeProducts = (orgProductsById: Record<string, VoiceScopeProduct> | undefined): VoiceScopeProductOf => skuOption => {
  if (!skuOption) return undefined;
  if (typeof skuOption !== 'string') return skuOption;
  return orgProductsById?.[skuOption];
};

const lineItemProduct = (skuOptions: (ScanTemplateSkuOption | string | undefined)[], productOf: VoiceScopeProductOf) =>
  skuOptions.map(productOf).find(product => !!product?.title);

export const lineItemPriceIsFromProduct = (lineItem: Pick<ScanLineItemOptimized, 'skuId' | 'template'>) =>
  !!lineItem.skuId || (lineItem.template.skuIdOptions?.length ?? 0) > 0;

const numericTemplateValues = (values: ScanTemplateValues | undefined): VoiceScopeSnapshotValue[] =>
  Object.entries(values ?? {})
    .filter(([, template]) => !NON_NUMERIC_VALUE_TYPES.has(template.type ?? ''))
    .map(([name, template]) => ({ name, type: template.type ?? 'count', description: template.description }));

const effectiveNumber = (part: ScanPartOptimized, valueName: string) => {
  const value = getEffectiveScanValue(part.values[valueName]);
  return typeof value === 'number' ? value : undefined;
};

const lineItemValueDescription = (option: ScanOptionOptimized, lineItem: ScanLineItemOptimized, spokenName: string) =>
  option.lineItems.length > 1 || lineItem.template.name !== option.template.name
    ? decodeEntities(`${option.template.name}: ${lineItem.template.name} ${spokenName}`)
    : decodeEntities(`${option.template.name} ${spokenName}`);

const isSpokenValueSettable = (lineItem: ScanLineItemOptimized, name: string) =>
  !!lineItem.template.values?.[name] && !(name === 'price' && lineItemPriceIsFromProduct(lineItem));

const lineItemValues = (task: ScanTaskOptimized): VoiceScopeSnapshotValue[] =>
  task.options.flatMap(option =>
    option.lineItems.flatMap(lineItem =>
      Object.entries(LINE_ITEM_SPOKEN_VALUES)
        .filter(([name]) => isSpokenValueSettable(lineItem, name))
        .map(([name, spokenName]) => ({
          name,
          type: lineItem.template.values?.[name]?.type ?? 'count',
          value: effectiveNumber(lineItem, name),
          description: lineItemValueDescription(option, lineItem, spokenName),
          optionId: option.id,
          lineItemId: lineItem.id,
        }))
    )
  );

const snapshotOption = (option: ScanOptionOptimized, lineItems: ScanLineItemOptimized[], productOf: VoiceScopeProductOf): VoiceScopeSnapshotOption => ({
  id: option.id,
  templateId: option.template.id,
  name: decodeEntities(option.template.name),
  lineItems: lineItems.map(lineItem => ({
    id: lineItem.id,
    templateId: lineItem.template.id,
    name: decodeEntities(lineItemProduct([lineItem.skuId, lineItem.template.skuIdOptions?.[0]], productOf)?.title ?? lineItem.template.name),
  })),
});

const scopedOptions = (task: ScanTaskOptimized, productOf: VoiceScopeProductOf): VoiceScopeSnapshotOption[] =>
  task.options
    .map(option => ({ option, lineItems: option.lineItems.filter(lineItem => isLineItemScoped(lineItem)) }))
    .filter(({ lineItems }) => lineItems.length > 0)
    .map(({ option, lineItems }) => snapshotOption(option, lineItems, productOf));

const snapshotTask = (task: ScanTaskOptimized, productOf: VoiceScopeProductOf): VoiceScopeSnapshotTask => ({
  id: task.id,
  templateId: task.template.id,
  name: decodeEntities(taskNamePresenter(task)),
  options: scopedOptions(task, productOf),
  photoCount: task.photos?.length ?? 0,
  totalCost: getTotalCost(task),
  values: [
    ...numericTemplateValues(task.template.values).map(value => ({ ...value, value: effectiveNumber(task, value.name) })),
    ...lineItemValues(task),
  ],
});

const catalogLineItem = (lineItem: ScanTemplateLineItem, productOf: VoiceScopeProductOf) => {
  const product = lineItemProduct([lineItem.skuIdOptions?.[0]], productOf);
  return {
    templateId: lineItem.id,
    name: decodeEntities(product?.title ?? lineItem.name),
    description: decodeOptional(product?.description ?? lineItem.description),
    isProduct: (lineItem.skuIdOptions?.length ?? 0) > 0,
  };
};

const catalogOption = (option: ScanTemplateOption, productOf: VoiceScopeProductOf): VoiceScopeCatalogOption => ({
  templateId: option.id,
  name: decodeEntities(option.name),
  description: decodeOptional(option.description),
  lineItems: (option.lineItemTemplates ?? []).map(lineItem => catalogLineItem(lineItem, productOf)),
});

const catalogTask = (template: ScanTemplateTask, existingTask: ScanTaskOptimized | undefined, productOf: VoiceScopeProductOf): VoiceScopeCatalogTask => ({
  templateId: template.id,
  name: decodeEntities(template.name),
  description: decodeOptional(template.description),
  existingTaskId: existingTask?.id,
  options: (template.optionTemplates ?? []).filter(option => !option.hidden).map(option => catalogOption(option, productOf)),
  values: numericTemplateValues(template.values),
});

export type BuildVoiceScopeSnapshotOptions = {
  version: number;
  taskId?: string;
  productOf?: VoiceScopeProductOf;
};

/** Builds the KaiAgent snapshot for the current group, optionally narrowed to one task. */
export const buildVoiceScopeSnapshot = (
  scanRoot: ScanRootOptimized,
  groupId: string,
  { version, taskId, productOf = noProducts }: BuildVoiceScopeSnapshotOptions,
): VoiceScopeSnapshot => {
  const group = scanRoot.groupsById[groupId];
  if (!group) throw new Error(`buildVoiceScopeSnapshot: group ${groupId} is not in the scope`);

  const tasksByTemplateId = new Map(group.tasks.map(task => [task.template.id, task]));
  const groupTasks = taskId
    ? (() => {
      const task = getScanTask(scanRoot, groupId, taskId);
      return task ? [snapshotTask(task, productOf)] : [];
    })()
    : group.tasks.map(task => snapshotTask(task, productOf));

  const catalogTemplates = taskId
    ? (() => {
      const task = getScanTask(scanRoot, groupId, taskId);
      return task ? [task.template] : [];
    })()
    : getAllScanTaskTemplatesForGroup(scanRoot.template, group.template);

  return {
    version,
    group: { id: group.id, name: decodeEntities(groupNamePresenter(group)), tasks: groupTasks },
    catalog: catalogTemplates.map(template => catalogTask(template, tasksByTemplateId.get(template.id), productOf)),
  };
};
